<template>
  <Dialog v-model:visible="isVisible" 
          modal 
          class="custom-threshold-dialog"
          :closable="false"
          :style="{width: '450px'}">
    <template #header>
      <div class="d-flex flex-column">
        <h5 class="mb-0 text-white font-bold">Alterar Limiar da Prática</h5>
        <small class="text-white">{{ exercise?.title }}</small>
      </div>
    </template>
    
    <div class="info-banner mt-4">
      <i class="pi pi-info-circle"></i>
      <span>O limiar define o tempo máximo esperado (em segundos) para a conclusão ideal desta atividade.</span>
    </div>
    
    <div class="field">
      <label for="threshold-input" class="fw-bold mb-2">Novo Limiar (segundos)</label>
      <InputNumber id="threshold-input" 
                   v-model="newThreshold" 
                   :min="1"
                   class="w-100" 
                   :useGrouping="false"/>
    </div>
    
    <template #footer>
      <button @click="closeModal" 
              class="btn btn-link text-decoration-none">
        Cancelar
      </button>
      <button @click="saveThreshold" 
              :disabled="!newThreshold || newThreshold <= 0 || isSavingThreshold"
              class="btn btn-primary d-flex align-items-center justify-content-center gap-2">
        <i :class="isSavingThreshold ? 'pi pi-spinner pi-spin' : 'pi pi-check-circle'"></i>
        <span>Salvar Alteração</span>
      </button>
    </template>
  </Dialog>
</template>

<script setup>
/* eslint-disable no-undef */
import {ref, watch, computed} from 'vue';
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import {useToast} from 'primevue/usetoast';
import PanelService from '@/services/panel.service';
import ErrorUtils from '@/utils/error.utils';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  exercise: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:visible', 'saved']);

const toast = useToast();
const newThreshold = ref(null);
const isSavingThreshold = ref(false);

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});

watch(() => props.exercise, (exercise) => {
  if (exercise) {
    newThreshold.value = exercise.threshold_seconds;
  }
});

function closeModal() {
  isVisible.value = false;
}

async function saveThreshold() {
  isSavingThreshold.value = true;
  
  try {
    await PanelService.updateExerciseThreshold(props.exercise.id, newThreshold.value);
    
    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Limiar atualizado com sucesso!',
      life: 3000
    });
    
    isVisible.value = false;
    emit('saved');
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: ErrorUtils.toMessage(error),
      life: 5000
    });
  } finally {
    isSavingThreshold.value = false;
  }
}
</script>

<style scoped>
.info-banner {
  background-color: #F0F7FF;
  border-left: 4px solid #3B82F6;
  padding: 12px 16px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
}

.info-banner i {
  color: #3B82F6;
  font-size: 1.1rem;
}

.info-banner span {
  color: #1e293b;
  font-size: 0.9rem;
  line-height: 1.5;
}
</style>

<style>
/* Estilos globais aplicados apenas ao modal de limiar (necessário pois o Dialog vai para o body) */
.custom-threshold-dialog {
  border: none !important;
  overflow: hidden !important; /* Força o header azul a respeitar os cantos arredondados do pai sem vazar fundos */
}

.custom-threshold-dialog .p-dialog-header {
  background-color: #1E5BB9 !important;
  border-bottom: none !important;
  padding: 1.5rem !important;
  border-top-left-radius: 6px !important;
  border-top-right-radius: 6px !important;
}

.custom-threshold-dialog .p-dialog-footer .btn-primary {
  background-color: #1E5BB9 !important;
  border-color: #1E5BB9 !important;
}

.custom-threshold-dialog .p-dialog-footer .btn-primary:hover {
  background-color: #154591 !important; /* Um tom levemente mais escuro para o hover */
  border-color: #154591 !important;
}
</style>
